import { useMemo, useRef, useState } from 'react';
import './App.css';
import newLogo from './assets/newlogo.png';
import heroTitleImage from './assets/design/hero-title.png';
import ctaKakoImage from './assets/design/cta-kako.png';
import customTitleImage from './assets/design/custom-title.png';
import customSubtitleImage from './assets/design/custom-subtitle.png';
import bullet1Image from './assets/design/bullet-1.png';
import bullet2Image from './assets/design/bullet-2.png';
import bullet3Image from './assets/design/bullet-3.png';
import ctaCustomImage from './assets/design/cta-custom.png';
import faqTitleImage from './assets/design/faq-title.png';
import faqQ1Image from './assets/design/faq-q1.png';
import faqQ2Image from './assets/design/faq-q2.png';
import faqQ3Image from './assets/design/faq-q3.png';
import faqQ4Image from './assets/design/faq-q4.png';
import faqQ5Image from './assets/design/faq-q5.png';
import bulletCheckImage from './assets/design/checkmark.png';
import bookImage from './imgs/landing_book.png';
import landingPattern from './imgs/landing_bg.png';
import {
  contactConfig,
  customHighlights,
  faqItems,
  processStages,
} from './content';

const bulletImages = [bullet1Image, bullet2Image, bullet3Image];
const faqQuestionImages = [faqQ1Image, faqQ2Image, faqQ3Image, faqQ4Image, faqQ5Image];

function buildMailtoHref(formData) {
  const subject = `${contactConfig.email.subjectPrefix} - ${formData.name || 'novi klijent'}`;
  const body = [
    `Ime: ${formData.name || '-'}`,
    `Povod: ${formData.occasion || '-'}`,
    `Detalji: ${formData.message || '-'}`,
  ].join('\n');

  return `mailto:${contactConfig.email.address}?subject=${encodeURIComponent(
    subject
  )}&body=${encodeURIComponent(body)}`;
}

function buildTelegramHref(formData) {
  const intro = [
    'Zdravo, zanima me custom Knjiga Cuda.',
    `Ime: ${formData.name || '-'}`,
    `Povod: ${formData.occasion || '-'}`,
    `Detalji: ${formData.message || '-'}`,
  ].join('\n');

  return `${contactConfig.telegram.baseUrl}?text=${encodeURIComponent(intro)}`;
}

function App() {
  const faqRef = useRef(null);
  const [expandedFaq, setExpandedFaq] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [ctaOpen, setCtaOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    occasion: '',
    message: '',
  });

  const mailtoHref = useMemo(() => buildMailtoHref(formData), [formData]);
  const telegramHref = useMemo(() => buildTelegramHref(formData), [formData]);

  const openFaqFlow = () => {
    setExpandedFaq(0);
    faqRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const updateField = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: value,
    }));
  };

  return (
    <div className="app-shell">
      <div className="phone-frame" style={{ '--pattern-image': `url(${landingPattern})` }}>
        <header className="topbar">
          <img className="logo-top" src={newLogo} alt="Knjiga Cuda" />

          <button
            className="menu-toggle"
            type="button"
            aria-expanded={menuOpen}
            aria-controls="contact-menu"
            aria-label="Otvori kontakt meni"
            onClick={() => setMenuOpen((current) => !current)}
          >
            <span />
            <span />
            <span />
          </button>
        </header>

        {menuOpen ? (
          <aside className="contact-menu" id="contact-menu">
            <a href={`mailto:${contactConfig.email.address}`}>
              {contactConfig.email.label}: {contactConfig.email.address}
            </a>
            <a href={contactConfig.telegram.baseUrl} target="_blank" rel="noreferrer">
              {contactConfig.telegram.label}: @{contactConfig.telegram.handle}
            </a>
          </aside>
        ) : null}

        <main className="page-content">
          <section className="hero">
            <div className="hero-copy">
              <img
                className="hero-title-image"
                src={heroTitleImage}
                alt="Deca brzo rastu. Napravi uspomene."
              />

              <button className="primary-button" type="button" onClick={openFaqFlow}>
                <span className="sr-only">Kako funkcionise?</span>
                <img src={ctaKakoImage} alt="" aria-hidden="true" />
              </button>
            </div>

            <div className="hero-visual">
              <img
                className="book-cover"
                src={bookImage}
                alt="Primer knjige Knjiga Cuda"
              />
            </div>
          </section>

          <div className="desktop-sections">
            <section className="custom-card-section">
              <article className="custom-card">
                <div className="custom-copy">
                  <img
                    className="custom-title-image"
                    src={customTitleImage}
                    alt="Custom knjiga cuda"
                  />
                  <img
                    className="custom-subtitle-image"
                    src={customSubtitleImage}
                    alt="Ne birate samo fotografije, vec i osecaj koji knjiga nosi."
                  />

                  <ul className="check-list">
                    {customHighlights.map((item, index) => (
                      <li key={`${item}-${index}`}>
                        <img
                          className="check-icon"
                          src={bulletCheckImage}
                          alt=""
                          aria-hidden="true"
                        />
                        <span className="sr-only">{item}</span>
                        <img
                          className="check-label-image"
                          src={bulletImages[index]}
                          alt=""
                          aria-hidden="true"
                        />
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="preview-card">
                  <img src={bookImage} alt="Mali pregled custom knjige" />
                </div>
              </article>

              <button
                className="cta-button"
                type="button"
                onClick={() => setCtaOpen(true)}
              >
                <span className="sr-only">Zelim custom knjigu cuda</span>
                <img src={ctaCustomImage} alt="" aria-hidden="true" />
              </button>

              <section className="process-panel" aria-labelledby="process-title">
                <p className="process-overline">Kako to izgleda</p>
                <h2 className="process-title" id="process-title">
                  Tri jasna koraka
                </h2>

                <div className="process-list">
                  {processStages.map((stage, index) => (
                    <article className="process-item" key={stage}>
                      <span className="process-index">0{index + 1}</span>
                      <p>{stage}</p>
                    </article>
                  ))}
                </div>
              </section>
            </section>

            <section className="faq-section" ref={faqRef}>
              <img
                className="faq-title-image"
                src={faqTitleImage}
                alt="Cesta pitanja"
              />

              <div className="faq-list">
                {faqItems.map((item, index) => {
                  const isOpen = expandedFaq === index;

                  return (
                    <article
                      className={`faq-item${isOpen ? ' faq-item-open' : ''}`}
                      key={item.question}
                    >
                      <button
                        className="faq-trigger"
                        type="button"
                      onClick={() => setExpandedFaq(isOpen ? -1 : index)}
                      aria-expanded={isOpen}
                    >
                      <span className="sr-only">{item.question}</span>
                      <img
                        className="faq-question-image"
                        src={faqQuestionImages[index]}
                        alt=""
                        aria-hidden="true"
                      />
                      <span className="faq-chevron">{isOpen ? '▴' : '▾'}</span>
                    </button>

                      {isOpen ? <p className="faq-answer">{item.answer}</p> : null}
                    </article>
                  );
                })}
              </div>
            </section>
          </div>
        </main>

        <footer className="footer">
          <img className="logo-footer" src={newLogo} alt="Knjiga Cuda" />
        </footer>
      </div>

      {ctaOpen ? (
        <div className="modal-backdrop" role="presentation" onClick={() => setCtaOpen(false)}>
          <section
            className="contact-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cta-title"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              className="modal-close"
              type="button"
              aria-label="Zatvori kontakt formu"
              onClick={() => setCtaOpen(false)}
            >
              ×
            </button>

            <h2 id="cta-title">Javite nam se za custom knjigu</h2>
            <p>
              Popunite osnovne detalje, pa izaberite kanal preko kog zelite da
              nastavite razgovor.
            </p>

            <form className="contact-form">
              <label>
                Ime
                <input
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={updateField}
                  placeholder="Vase ime"
                />
              </label>

              <label>
                Povod
                <input
                  name="occasion"
                  type="text"
                  value={formData.occasion}
                  onChange={updateField}
                  placeholder="Rodjendan, poklon, vencanje..."
                />
              </label>

              <label>
                Kratak opis
                <textarea
                  name="message"
                  rows="4"
                  value={formData.message}
                  onChange={updateField}
                  placeholder="Broj fotografija, rok, ideja..."
                />
              </label>
            </form>

            <div className="modal-actions">
              <a className="modal-link" href={mailtoHref}>
                Nastavi preko emaila
              </a>
              <a
                className="modal-link modal-link-secondary"
                href={telegramHref}
                target="_blank"
                rel="noreferrer"
              >
                Nastavi preko Telegrama
              </a>
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}

export default App;
